import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Edit } from "react-admin";
import { CouponType } from "../../enums/CouponType";
import { ProductType } from "../../enums/ProductType";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { CouponSkuService } from "../../services/CouponSkuService";
import CircularLoader from "../Common/CircularLoader";
import { TimeEnum } from "../../enums/TimeEnum";
import { useSlotSizeOptions } from "../../utils/SlotSizeOptions";

const couponSkuService = new CouponSkuService();

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6C7C93", letterSpacing: "0.5px", textTransform: "uppercase", mb: 1.5, fontFamily: "inherit" }}>
    {children}
  </Typography>
);

const EditCouponSku = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [skuData, setSkuData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [timeInclusionExclusion, setTimeInclusionExclusion] = useState<boolean>(false);
  const [addTimeInclusion, setAddTimeInclusion] = useState<boolean>(false);
  const [selectedTimeEnums, setSelectedTimeEnums] = useState<any>([]);
  const slotSizeOptions = useSlotSizeOptions();

  useEffect(() => {
    couponSkuService.getSkuById(Number(id))
      .then((res) => {
        setSkuData(res?.data);
      })
      .catch(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Failed to load coupon SKU",
          type: CustomSnackbarTypes.ERROR,
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const validate = () => {
    const errors: any = {};
    if (!skuData.name) errors.name = "Name is required";
    if (!skuData.description) errors.description = "Description is required";
    if (Number(skuData.price) < 0) errors.price = "Price must be >= 0";
    if (Number(skuData.value) < 0) errors.value = "Value must be >= 0";
    if (!skuData.couponType) errors.couponType = "Coupon type required";
    if (Number(skuData.numberOfDays) < 0) errors.numberOfDays = "Days must be >= 0";
    if (!skuData.productType) errors.productType = "Product type required";
    if (!skuData.minimumOrderValue || Number(skuData.minimumOrderValue) < 0)
      errors.minimumOrderValue = "Minimum order value must be >= 0";
    if (!skuData.maximumDiscountAmount || Number(skuData.maximumDiscountAmount) < 0)
      errors.maximumDiscountAmount = "Maximum discount must be >= 0";
    if (Number(skuData.maximumUsageLimit) < 0)
      errors.maximumUsageLimit = "Usage limit must be >= 0";
    return errors;
  };

  const handleSubmit = () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Please fix validation errors",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    const payload = {
      name: skuData.name,
      description: skuData.description,
      price: skuData.price,
      value: skuData.value,
      couponType: skuData.couponType,
      numberOfDays: Number(skuData.numberOfDays),
      productType: skuData.productType,
      minimumOrderValue: skuData.minimumOrderValue,
      maximumDiscountAmount: skuData.maximumDiscountAmount,
      maximumUsageLimit: Number(skuData.maximumUsageLimit),
      isActive: skuData.isActive === undefined ? true : skuData.isActive,
      noOfPeople: Number(skuData.noOfPeople) || 0,
      isCustomCombo: false,
      slotSize:
        skuData.slotSize !== null &&
        skuData.slotSize !== undefined &&
        skuData.slotSize !== ""
          ? Number(skuData.slotSize)
          : null,
      isPopular: skuData.isPopular || false,
      isRenewable: skuData.isRenewable ?? null,
      packTag: skuData.packTag || null,
      timeInclusionExclusionRule: timeInclusionExclusion
        ? { type: addTimeInclusion ? "inclusion" : "exclusion", timeSlots: selectedTimeEnums }
        : skuData?.timeInclusionExclusionRule,
    };

    couponSkuService.updateCouponSku(Number(id), payload)
      .then(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Coupon SKU updated successfully",
          type: CustomSnackbarTypes.SUCCESS,
        });
        navigate(`/CouponSku/${id}/show`);
      })
      .catch(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Failed to update",
          type: CustomSnackbarTypes.ERROR,
        });
      });
  };

  if (loading || !skuData) return <CircularLoader />;

  // Keep the SKU's saved slot size selectable even if it was dropped from the configured options
  const savedSlotSize =
    skuData.slotSize !== null && skuData.slotSize !== undefined && skuData.slotSize !== ""
      ? Number(skuData.slotSize)
      : null;
  const editSlotSizeOptions =
    savedSlotSize !== null && !slotSizeOptions.includes(savedSlotSize)
      ? [...slotSizeOptions, savedSlotSize].sort((a, b) => a - b)
      : slotSizeOptions;

  return (
    <Edit title="Edit Coupon SKU">
      <Box sx={{ padding: "24px", display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* ── Text Fields ── */}
        <Box>
          <SectionLabel>Basic Information</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <TextField
              label="Name"
              value={skuData.name ?? ""}
              onChange={(e) => setSkuData({ ...skuData, name: e.target.value })}
            />
            <TextField
              label="Description"
              value={skuData.description ?? ""}
              onChange={(e) => setSkuData({ ...skuData, description: e.target.value })}
            />
            <TextField
              label="Pack Tag"
              value={skuData.packTag ?? ""}
              onChange={(e) => setSkuData({ ...skuData, packTag: e.target.value })}
            />
          </Box>
        </Box>

        <Box>
          <SectionLabel>Pricing & Limits</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <TextField
              label="Price"
              value={skuData.price ?? ""}
              onChange={(e) => setSkuData({ ...skuData, price: e.target.value })}
            />
            <TextField
              label="Value"
              value={skuData.value ?? ""}
              onChange={(e) => setSkuData({ ...skuData, value: e.target.value })}
            />
            <TextField
              label="Minimum Order Value"
              value={skuData.minimumOrderValue ?? ""}
              onChange={(e) => setSkuData({ ...skuData, minimumOrderValue: e.target.value })}
            />
            <TextField
              label="Maximum Discount Amount"
              value={skuData.maximumDiscountAmount ?? ""}
              onChange={(e) => setSkuData({ ...skuData, maximumDiscountAmount: e.target.value })}
            />
            <TextField
              label="Maximum Usage Limit"
              type="number"
              value={skuData.maximumUsageLimit ?? 0}
              onChange={(e) => setSkuData({ ...skuData, maximumUsageLimit: Number(e.target.value) })}
            />
            <TextField
              label="Number of Days"
              type="number"
              value={skuData.numberOfDays ?? 0}
              onChange={(e) => setSkuData({ ...skuData, numberOfDays: Number(e.target.value) })}
            />
            <TextField
              label="No of People"
              type="number"
              value={skuData.noOfPeople ?? 0}
              onChange={(e) => setSkuData({ ...skuData, noOfPeople: Number(e.target.value) })}
            />
            <FormControl fullWidth>
              <InputLabel>Slot Size (Hours)</InputLabel>
              <Select
                // API returns slotSize as a decimal string ("1.5"), MenuItem values are numbers
                value={skuData.slotSize !== null && skuData.slotSize !== undefined && skuData.slotSize !== "" ? Number(skuData.slotSize) : ""}
                label="Slot Size (Hours)"
                onChange={(e) => setSkuData({ ...skuData, slotSize: e.target.value === "" ? null : Number(e.target.value) })}
              >
                <MenuItem value="">None</MenuItem>
                {editSlotSizeOptions.map((v) => (
                  <MenuItem key={v} value={v}>{v} hr{v !== 1 ? "s" : ""}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider />

        {/* ── Selects ── */}
        <Box>
          <SectionLabel>Type Options</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Coupon Type</InputLabel>
              <Select
                value={skuData.couponType ?? ""}
                label="Coupon Type"
                onChange={(e) => setSkuData({ ...skuData, couponType: e.target.value })}
              >
                {Object.values(CouponType).map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Product Type</InputLabel>
              <Select
                value={Object.values(ProductType).includes(skuData.productType) ? skuData.productType : ""}
                label="Product Type"
                onChange={(e) => setSkuData({ ...skuData, productType: e.target.value })}
              >
                {Object.values(ProductType).map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider />

        {/* ── Radio Groups — all in one row ── */}
        <Box>
          <SectionLabel>Flags</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} alignItems="flex-start">
            <FormControl>
              <FormLabel>Is Active</FormLabel>
              <RadioGroup
                row
                value={skuData.isActive ? "true" : "false"}
                onChange={(e) => setSkuData({ ...skuData, isActive: e.target.value === "true" })}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Is Popular</FormLabel>
              <RadioGroup
                row
                value={skuData.isPopular ? "true" : "false"}
                onChange={(e) => setSkuData({ ...skuData, isPopular: e.target.value === "true" })}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Is Renewable</FormLabel>
              <RadioGroup
                row
                value={skuData.isRenewable === null || skuData.isRenewable === undefined ? "" : skuData.isRenewable ? "true" : "false"}
                onChange={(e) => setSkuData({ ...skuData, isRenewable: e.target.value === "true" })}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>{skuData?.timeInclusionExclusionRule ? "Change Time Inclusion Exclusion Rule" : "Add Time Inclusion Exclusion Rule"}</FormLabel>
              <RadioGroup
                row
                value={timeInclusionExclusion ? "true" : "false"}
                onChange={(e) => setTimeInclusionExclusion(e.target.value === "true")}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        {/* Current Rule info */}
        {skuData?.timeInclusionExclusionRule && (
          <Box sx={{ backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "12px 16px" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 1, fontFamily: "inherit" }}>
              Current Time Rule: {skuData.timeInclusionExclusionRule.type === "inclusion" ? "Inclusion" : "Exclusion"}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {skuData.timeInclusionExclusionRule.timeSlots?.map((slot: any, index: number) => (
                <Chip key={index} color="primary" label={slot} />
              ))}
            </Box>
          </Box>
        )}

        {/* New Rule selection */}
        {timeInclusionExclusion && (
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl>
              <FormLabel>Inclusion / Exclusion</FormLabel>
              <RadioGroup
                row
                value={addTimeInclusion ? "true" : "false"}
                onChange={(e) => setAddTimeInclusion(e.target.value === "true")}
              >
                <FormControlLabel value="true" control={<Radio />} label="Inclusion" />
                <FormControlLabel value="false" control={<Radio />} label="Exclusion" />
              </RadioGroup>
            </FormControl>
            <FormControl sx={{ minWidth: "300px" }}>
              <InputLabel>Time Slots</InputLabel>
              <Select
                multiple
                value={selectedTimeEnums}
                label="Time Slots"
                onChange={(e) => setSelectedTimeEnums(e.target.value)}
                renderValue={(selected: TimeEnum[]) => (
                  <Box sx={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                    {selected.map((value) => (
                      <Chip key={value} color="primary" label={value} />
                    ))}
                  </Box>
                )}
              >
                {Object.entries(TimeEnum).map(([key, value]) => (
                  <MenuItem key={key} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Divider />

        <Box>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ minWidth: "200px", padding: "10px 24px", fontSize: "14px", textTransform: "none", borderRadius: "6px" }}
          >
            Update Coupon SKU
          </Button>
        </Box>

      </Box>
    </Edit>
  );
};

export default EditCouponSku;
